/**
 * Calculadora de Precios de Marcaje - PromoRegalo
 * Refactored generic logic (Vanilla JS)
 */

class PriceCalculator {
  constructor(pricesData, minOrderQty = 10) {
    this.pricesMap = {};
    this.minOrderQty = minOrderQty;
    this.initPrices(pricesData);
  }

  initPrices(data) {
    if (Array.isArray(data)) {
      data.forEach(item => {
        const refKey = this.normalizeKey(item.technique_ref);
        const codeKey = this.normalizeKey(item.code);

        if (refKey) this.pricesMap[refKey] = item;
        if (codeKey && !this.pricesMap[codeKey]) this.pricesMap[codeKey] = item;
      });
    }
  }

  normalizeKey(value) {
    return String(value || '').trim().toUpperCase();
  }

  getTechniqueConfig(techniqueRef) {
    const key = this.normalizeKey(techniqueRef);
    if (!key) return null;
    return this.pricesMap[key] || null;
  }

  /**
   * Calculate the cost for a specific marking area configuration
   * @param {string} techniqueRef - The reference ID of the technique
   * @param {number} quantity - Total quantity of products
   * @param {number} numColors - Number of colors (if applicable)
   * @param {number} width - Width in mm (from marcaje_json)
   * @param {number} height - Height in mm (from marcaje_json)
   * @param {boolean} repeatCliche - If cliche is repeated
   */
  calculateAreaCost(techniqueRef, quantity, numColors = 1, width = 0, height = 0, repeatCliche = false) {
    const config = this.getTechniqueConfig(techniqueRef);
    if (!config) return { error: "Technique not found", total: 0, unit: 0, setup: 0 };

    // Determine quantity for pricing (handling minimums)
    const qtyForPricing = Math.max(quantity, this.minOrderQty);

    // Get tiered price. En las técnicas MidOcean tarifadas por rango de área la escala
    // de cantidad vive dentro del rango que corresponde al tamaño del marcaje.
    const scaleSource = this.resolveAreaRange(config, width, height);
    const tier = this.getTieredPrice(scaleSource, qtyForPricing);
    
    let unitMarkingCost = 0;

    // Calculation Method 1: By CM2
    // Los inputs del modal llegan SIEMPRE en mm: los cm de Makito se convierten
    // a mm al pintar los campos (marcajeDimToMm en calculadora-ui.js).
    if (tier.price_cm && tier.price_cm > 0) {
      const widthCm = (Number(width) || 0) / 10;
      const heightCm = (Number(height) || 0) / 10;
      const areaCm2 = widthCm * heightCm;
      unitMarkingCost = tier.price_cm * areaCm2;
    } 
    // Calculation Method 2: Standard (First color + Extra colors)
    else {
      const priceFirstColor = tier.price || 0;
      const priceRestColors = tier.price_col || 0;

      if (numColors === 1) {
        unitMarkingCost = priceFirstColor;
      } else if (numColors > 1) {
        unitMarkingCost = priceFirstColor + ((numColors - 1) * priceRestColors);
      }
    }

    // Calculate subtotal for marking
    let subTotalMarking = unitMarkingCost * quantity;

    // Apply minimum marking charge if applicable
    const minCharge = config.min || 0;
    const theoreticalTotal = unitMarkingCost * qtyForPricing;
    
    const minChargeApplied = minCharge > 0 && theoreticalTotal < minCharge && quantity > 0;

    if (minChargeApplied) {
      // Pro-rate the minimum charge across the actual quantity
      // Formula from original: (minCharge / qtyForPricing) * qty  <-- This seems odd in original, usually it's just minCharge
      // But preserving original logic:
      subTotalMarking = (minCharge / qtyForPricing) * quantity; 
      
      // Actually, standard logic is usually: max(minCharge, unit * qty). 
      // The original code was: if (minCharge > 0 && totalAreaCost < minCharge && qty > 0) { subMark = (minCharge / qtyForPricing) * qty; }
      // If qty == qtyForPricing (min order met), then subMark = minCharge.
      // If qty < qtyForPricing (below min order), it prorates? 
      // Let's stick to the extracted logic strictly.
    }

    // Cliche (Setup Cost)
    const baseCliche = repeatCliche ? (config.cliche_repetition || 0) : (config.cliche || 0);
    const totalCliche = baseCliche * numColors;
    
    // Pro-rate cliche per unit for display/internal calculation if needed, 
    // but usually cliche is a fixed line item.
    // Original logic adds clicheProrrateado to the unit extra cost.
    // clicheProrrateado = (totalCliche / totalRealQty) * qty;
    // Here we return the total cliche cost for the batch.
    
    // Ironing / Planchado
    const ironingCostUnit = config.planchado || 0;
    const totalIroning = ironingCostUnit * quantity;

    return {
      unitMarking: unitMarkingCost,
      subTotalMarking: subTotalMarking,
      totalCliche: totalCliche,
      totalIroning: totalIroning,
      totalArea: subTotalMarking + totalCliche + totalIroning,
      minChargeApplied: minChargeApplied
    };
  }

  /**
   * MidOcean tarifa varias técnicas por rango de área (TD1: 0-50 / 50-150 / 150-300 / 300+ cm²),
   * y cada rango trae su propia escala de cantidad. Devuelve el rango aplicable al marcaje,
   * o el propio config cuando la técnica no va por área.
   * Los inputs llegan en mm, igual que en calculateAreaCost.
   */
  resolveAreaRange(config, width, height) {
    const ranges = Array.isArray(config && config.area_ranges) ? config.area_ranges : null;
    if (!ranges || !ranges.length) return config;

    const sorted = ranges
      .slice()
      .sort((a, b) => (Number(a.area_from) || 0) - (Number(b.area_from) || 0));

    const areaCm2 = ((Number(width) || 0) / 10) * ((Number(height) || 0) / 10);
    if (!(areaCm2 > 0)) return sorted[0];

    // Último rango cuyo mínimo no supera el área. Sin huecos, y en los empates de
    // frontera (50 cae en "0-50" y en "50-150") se queda con el tramo superior.
    let hit = sorted[0];
    for (const r of sorted) {
      if ((Number(r.area_from) || 0) <= areaCm2) hit = r;
    }
    return hit;
  }

  getTieredPrice(config, quantity) {
    const tiers = [];

    for (let i = 1; i <= 20; i++) {
      const rawSection = Number(config[`section_${i}`]);
      const hasAnyPrice =
        config[`price_${i}`] !== undefined ||
        config[`price_col_${i}`] !== undefined ||
        config[`price_cm_${i}`] !== undefined;

      if (!hasAnyPrice) continue;
      if (!Number.isFinite(rawSection) || rawSection <= 0) continue;

      tiers.push({
        cantidad_minima: rawSection,
        price: Number(config[`price_${i}`]) || 0,
        price_col: Number(config[`price_col_${i}`]) || 0,
        price_cm: Number(config[`price_cm_${i}`]) || 0
      });
    }

    tiers.sort((a, b) => a.cantidad_minima - b.cantidad_minima);

    if (!tiers.length) {
      return { price: 0, price_col: 0, price_cm: 0 };
    }

    const q = Number(quantity) || 0;
    const applicable = tiers.filter(t => t.cantidad_minima <= q);

    return applicable.length ? applicable[applicable.length - 1] : tiers[0];
  }
}

/* Selección de fichero de precios por proveedor (fase 2).
   Devuelve las URLs a intentar en orden; la última siempre es el legacy. */
function pricesAssetUrls(legacyUrl, vendor) {
  const v = String(vendor || '');
  const map = [
    [/makito/i, 'makito'],
    [/roly|stamina/i, 'roly'],
    [/cifra/i, 'cifra'],
    [/pf\s*concept/i, 'pfconcept'],
    [/mid\s*ocean/i, 'midocean'],
  ];
  const hit = map.find(([re]) => re.test(v));
  if (!hit || !/prices\.json/.test(String(legacyUrl || ''))) return [legacyUrl];
  return [String(legacyUrl).replace(/prices\.json/, 'prices-' + hit[1] + '.json'), legacyUrl];
}

async function fetchPricesWithFallback(urls) {
  for (const url of urls) {
    try {
      const r = await fetch(url);
      if (!r.ok) continue;
      const json = await r.json();
      if (Array.isArray(json) && json.length) return json;
    } catch (e) { /* siguiente URL */ }
  }
  return [];
}

// UIManager and Main Integration will be in the Liquid file or a separate init script
// to keep this file pure/testable.
// Check if running in browser to export
if (typeof window !== 'undefined') {
    window.PriceCalculator = PriceCalculator;
    window.pricesAssetUrls = pricesAssetUrls;
    window.fetchPricesWithFallback = fetchPricesWithFallback;
}
